/**
 * HR Phase D — Intelligence + Risk Alerts panels.
 * Extracted from SchoolHR.jsx.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Loader2, Sparkles, TrendingUp, FileSignature, UserPlus, Cake, AlertTriangle,
  CalendarDays, DollarSign, Star
} from 'lucide-react';
import { schoolApi as api } from '@/components/school/schoolApi';

const IntelligencePanel = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="hr-intelligence-panel">
      <SmartDirectoryCard tenantId={tenantId} />
      <div className="grid md:grid-cols-2 gap-4">
        <AttendanceRiskCard tenantId={tenantId} />
        <LeaveOverlapCard tenantId={tenantId} />
      </div>
      <PayrollInsightsCard tenantId={tenantId} />
      <PerformanceLeaderboardCard tenantId={tenantId} />
    </div>
  );
};

const SmartDirectoryCard = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['hr-smart-directory', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/staff-directory/smart`),
  });
  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const d = data || {};
  const Section = ({ title, icon: Icon, items, colorClass, emptyText, render }) => (
    <div className={`p-3 rounded-lg border ${colorClass}`}>
      <div className="flex items-center gap-2 mb-2 font-medium text-sm"><Icon className="h-4 w-4" /> {title} <Badge variant="outline" className="ml-1 bg-white/60">{items.length}</Badge></div>
      {items.length === 0 ? <p className="text-xs text-slate-500">{emptyText}</p> :
       <div className="space-y-1.5">{items.slice(0, 5).map((it, i) => <div key={i} className="text-xs">{render(it)}</div>)}</div>}
    </div>
  );
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-indigo-600" /> Smart Staff Directory</CardTitle>
        <CardDescription>Upcoming events in the next {d.within_days || 30} days · {d.total_active_staff || 0} active staff</CardDescription>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Section title="Anniversaries" icon={TrendingUp} items={d.upcoming_anniversaries || []} colorClass="bg-indigo-50 border-indigo-200" emptyText="None coming up" render={(a) => <div data-testid={`anniv-${a.staff_id}`}><b>{a.name}</b> · {a.years}y · {a.date}</div>} />
        <Section title="Contracts Expiring" icon={FileSignature} items={d.contracts_expiring || []} colorClass="bg-rose-50 border-rose-200" emptyText="No expiring contracts" render={(c) => <div data-testid={`contract-${c.staff_id}`}><b>{c.name}</b> · in {c.days_left}d</div>} />
        <Section title="New Hires" icon={UserPlus} items={d.new_hires_last_30d || []} colorClass="bg-emerald-50 border-emerald-200" emptyText="No new hires recently" render={(h) => <div data-testid={`newhire-${h.staff_id}`}><b>{h.name}</b> · {h.days_since}d ago</div>} />
        <Section title="Birthdays" icon={Cake} items={d.upcoming_birthdays || []} colorClass="bg-amber-50 border-amber-200" emptyText="No birthdays" render={(b) => <div data-testid={`bday-${b.staff_id}`}><b>{b.name}</b> · {b.date} · 🎂{b.turning}</div>} />
      </CardContent>
    </Card>
  );
};

const AttendanceRiskCard = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['hr-att-risk', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/attendance/risk-staff`),
  });
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-4 w-4 text-rose-600" /> Attendance Risks</CardTitle>
        <CardDescription>Staff below {data?.threshold_percent || 80}% presence in last {data?.window_days || 30}d</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
         (data?.staff || []).length === 0 ? <p className="text-sm text-emerald-700 bg-emerald-50 p-2 rounded border border-emerald-200" data-testid="att-risk-empty">✓ Everyone is above threshold</p> :
         <div className="space-y-1.5">
          {(data?.staff || []).slice(0, 8).map(s => (
            <div key={s.staff_id} className="flex items-center justify-between p-2 rounded-md border bg-rose-50" data-testid={`att-risk-${s.staff_id}`}>
              <div><div className="text-sm font-medium">{s.name}</div><div className="text-xs text-slate-600">{s.flags.join(' · ')}</div></div>
              <Badge variant="destructive">{s.presence_percent}%</Badge>
            </div>
          ))}
         </div>}
      </CardContent>
    </Card>
  );
};

const LeaveOverlapCard = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['hr-leave-overlap', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/leave/overlap-warnings`),
  });
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><CalendarDays className="h-4 w-4 text-amber-600" /> Leave Overlap Warnings</CardTitle>
        <CardDescription>Days where &gt;{data?.threshold_percent || 20}% of a department is on leave</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
         (data?.warnings || []).length === 0 ? <p className="text-sm text-emerald-700 bg-emerald-50 p-2 rounded border border-emerald-200" data-testid="overlap-empty">✓ No overlapping leaves</p> :
         <div className="space-y-1.5">
          {(data?.warnings || []).slice(0, 8).map((w, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-md border bg-amber-50" data-testid={`overlap-${i}`}>
              <div><div className="text-sm font-medium">{w.department_name}</div><div className="text-xs text-slate-600">{w.date} · {w.on_leave}/{w.total_staff} on leave</div></div>
              <Badge className="bg-amber-200 text-amber-900">{w.percent}%</Badge>
            </div>
          ))}
         </div>}
      </CardContent>
    </Card>
  );
};

const PayrollInsightsCard = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['hr-payroll-insights', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/payroll/insights`),
  });
  if (isLoading) return <Card><CardContent className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>;
  const d = data || {};
  const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><DollarSign className="h-4 w-4 text-emerald-600" /> Payroll Insights</CardTitle>
        <CardDescription>Last 6 months · Projected monthly cost: <b>{fmt(d.projected_monthly)}</b></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg border bg-emerald-50"><div className="text-xs text-emerald-700">Paid (6m)</div><div className="text-2xl font-bold text-emerald-900" data-testid="payroll-paid">{fmt(d.total_paid_6m)}</div></div>
          <div className="p-3 rounded-lg border bg-amber-50"><div className="text-xs text-amber-700">Pending (6m)</div><div className="text-2xl font-bold text-amber-900" data-testid="payroll-pending">{fmt(d.total_pending_6m)}</div></div>
          <div className="p-3 rounded-lg border bg-indigo-50"><div className="text-xs text-indigo-700">Runs</div><div className="text-2xl font-bold text-indigo-900" data-testid="payroll-runs">{d.runs_count}</div></div>
          <div className="p-3 rounded-lg border bg-slate-50"><div className="text-xs text-slate-700">Monthly Projected</div><div className="text-2xl font-bold text-slate-900" data-testid="payroll-projected">{fmt(d.projected_monthly)}</div></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <h4 className="text-sm font-semibold mb-2">Top Department Costs</h4>
            <div className="space-y-1">
              {(d.department_costs || []).slice(0, 5).map((dc) => (
                <div key={dc.department_id} className="flex items-center justify-between text-xs p-2 rounded border bg-slate-50" data-testid={`dept-cost-${dc.department_id}`}>
                  <span>{dc.department_name}</span>
                  <b>{fmt(dc.monthly_cost)}</b>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-2">Salary Band Distribution</h4>
            <div className="space-y-1">
              {Object.entries(d.salary_bands || {}).map(([band, count]) => (
                <div key={band} className="flex items-center justify-between text-xs">
                  <span className="w-20">{band}</span>
                  <div className="flex-1 mx-2 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, (count / Math.max(1, Math.max(...Object.values(d.salary_bands || {})))) * 100)}%` }} /></div>
                  <b className="w-6 text-right">{count}</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PerformanceLeaderboardCard = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['hr-perf-leaderboard', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/performance/leaderboard`),
  });
  const tierColor = { excellent: 'bg-emerald-100 text-emerald-800', good: 'bg-indigo-100 text-indigo-800', needs_improvement: 'bg-amber-100 text-amber-800', at_risk: 'bg-rose-100 text-rose-800' };
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Star className="h-4 w-4 text-indigo-600" /> Teacher Performance Leaderboard</CardTitle>
        <CardDescription>Composite score: 30% attendance · 30% syllabus · 20% engagement · 20% reviews</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
         (data?.leaderboard || []).length === 0 ? <p className="text-sm text-slate-500" data-testid="perf-leaderboard-empty">No performance data yet.</p> :
         <Table>
           <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Teacher</TableHead><TableHead>Attendance</TableHead><TableHead>Syllabus</TableHead><TableHead>Engagement</TableHead><TableHead>Review</TableHead><TableHead>Score</TableHead><TableHead>Tier</TableHead></TableRow></TableHeader>
           <TableBody>
             {(data?.leaderboard || []).slice(0, 15).map((r, i) => (
               <TableRow key={r.teacher_id} data-testid={`perf-row-${r.teacher_id}`}>
                 <TableCell className="text-xs">#{i+1}</TableCell>
                 <TableCell className="font-medium text-sm">{r.teacher_name.trim() || '—'}</TableCell>
                 <TableCell className="text-xs">{r.factors.attendance ?? '—'}{r.factors.attendance != null && '%'}</TableCell>
                 <TableCell className="text-xs">{r.factors.syllabus ?? '—'}{r.factors.syllabus != null && '%'}</TableCell>
                 <TableCell className="text-xs">{r.factors.engagement ?? '—'}</TableCell>
                 <TableCell className="text-xs">{r.factors.review ?? '—'}{r.factors.review != null && '%'}</TableCell>
                 <TableCell><Badge className="bg-slate-100 text-slate-900">{r.composite_score}</Badge></TableCell>
                 <TableCell><Badge className={tierColor[r.tier] || ''}>{(r.tier || '').replace('_',' ')}</Badge></TableCell>
               </TableRow>
             ))}
           </TableBody>
         </Table>}
      </CardContent>
    </Card>
  );
};

const RiskAlertsPanel = ({ tenantId }) => {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['hr-risk-alerts', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/risk-alerts`),
  });
  const typeColor = { attendance: 'bg-rose-100 text-rose-700', contract: 'bg-amber-100 text-amber-700', performance: 'bg-purple-100 text-purple-700', leave_overload: 'bg-blue-100 text-blue-700' };
  const sevColor = { high: 'bg-rose-600 text-white', medium: 'bg-amber-500 text-white', low: 'bg-slate-400 text-white' };
  return (
    <Card data-testid="risk-alerts-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-5 w-5 text-rose-600" /> Unified Risk Alerts</CardTitle>
          <CardDescription>Staff-at-risk feed · attendance · contracts · performance · leave overload</CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="risk-alerts-refresh">
          {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div> :
         (data?.alerts || []).length === 0 ? <p className="text-center py-8 text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="risk-alerts-empty">✓ No active risk alerts</p> :
         <div className="space-y-2">
           {(data?.alerts || []).map((a, i) => (
             <div key={i} className="flex items-center justify-between p-3 rounded-md border bg-white hover:bg-slate-50 transition" data-testid={`risk-alert-${i}`}>
               <div className="flex items-center gap-3">
                 <Badge className={sevColor[a.severity] || ''}>{a.severity}</Badge>
                 <Badge className={typeColor[a.type] || ''} variant="outline">{a.type.replace('_',' ')}</Badge>
                 <div>
                   <div className="text-sm font-medium">{a.name || '—'}</div>
                   <div className="text-xs text-slate-600">{a.summary}</div>
                 </div>
               </div>
             </div>
           ))}
         </div>}
      </CardContent>
    </Card>
  );
};


export {
  IntelligencePanel,
  SmartDirectoryCard,
  AttendanceRiskCard,
  LeaveOverlapCard,
  PayrollInsightsCard,
  PerformanceLeaderboardCard,
  RiskAlertsPanel,
};
